/*!
 * xn-analytics.js — XNails first-party, cookieless analytics pixel.
 *
 * Portable, performance-first. One deferred, CDN-cached request + tiny JS.
 * Everything runs inside try/catch and off the critical path so it can never
 * break or slow the storefront. No cookies, no PII: a visitor id lives in
 * localStorage and a session id in sessionStorage.
 *
 * Transport: navigator.sendBeacon(ENDPOINT, Blob([json], {type:'text/plain'})).
 * text/plain (NOT application/json) keeps the cross-origin POST a CORS "simple
 * request" so the browser skips the preflight OPTIONS round-trip. Falls back to
 * fetch(..., {keepalive:true}) when sendBeacon is unavailable.
 */
(function () {
  "use strict";
  try {
    var ENDPOINT = "https://admin.xnails.se/api/collect";
    var PROPERTY = "xnails-se";
    var FLUSH_MS = 4000;
    var MAX_BATCH = 20;
    var MIN_ENGAGE_MS = 1000;

    // ── storage helpers (never throw) ─────────────────────────────────────
    function safeGet(store, key) {
      try { return store.getItem(key); } catch (e) { return null; }
    }
    function safeSet(store, key, val) {
      try { store.setItem(key, val); } catch (e) {}
    }
    function uuid() {
      try {
        if (window.crypto && crypto.randomUUID) { return crypto.randomUUID(); }
      } catch (e) {}
      return (
        "xn-" + Date.now().toString(36) + "-" +
        Math.random().toString(36).slice(2, 10) +
        Math.random().toString(36).slice(2, 10)
      );
    }

    // Cookieless identity.
    var anonId = safeGet(localStorage, "xn:vid");
    if (!anonId) { anonId = uuid(); safeSet(localStorage, "xn:vid", anonId); }
    var sessionId = safeGet(sessionStorage, "xn:sid");
    if (!sessionId) { sessionId = uuid(); safeSet(sessionStorage, "xn:sid", sessionId); }

    // ── context capture ───────────────────────────────────────────────────
    function localeInfo() {
      var locale = null, country = null;
      try {
        if (window.Shopify) {
          if (Shopify.locale) { locale = String(Shopify.locale); }
          if (Shopify.country) { country = String(Shopify.country); }
        }
      } catch (e) {}
      try {
        if (!locale && document.documentElement && document.documentElement.lang) {
          locale = String(document.documentElement.lang);
        }
      } catch (e) {}
      try {
        // Leading /xx/ market segment (Shopify Markets localized paths).
        var seg = location.pathname.split("/")[1];
        if (seg && /^[a-z]{2}(-[a-z]{2})?$/i.test(seg)) {
          if (!locale) { locale = seg; }
          if (!country) { country = seg.slice(0, 2).toUpperCase(); }
        }
      } catch (e) {}
      // Fold country into locale string when we have both but locale lacks region.
      if (locale && country && locale.indexOf("-") === -1) {
        return locale + "-" + country;
      }
      return locale || country || null;
    }

    function referrerHost() {
      try {
        if (!document.referrer) { return null; }
        var u = new URL(document.referrer);
        return u.host !== location.host ? u.host : null;
      } catch (e) { return null; }
    }

    function utm() {
      var out = {
        utmSource: null, utmMedium: null, utmCampaign: null, utmContent: null
      };
      try {
        var q = new URLSearchParams(location.search);
        out.utmSource = q.get("utm_source");
        out.utmMedium = q.get("utm_medium");
        out.utmCampaign = q.get("utm_campaign");
        out.utmContent = q.get("utm_content");
      } catch (e) {}
      return out;
    }

    // Detect page type + handle from Shopify globals, body class, or URL.
    function pageContext() {
      var type = null, handle = null;
      try {
        var t = window.ShopifyAnalytics &&
          ShopifyAnalytics.meta && ShopifyAnalytics.meta.page &&
          ShopifyAnalytics.meta.page.pageType;
        if (t) { type = String(t); }
      } catch (e) {}
      var path = location.pathname;
      try {
        var pm = path.match(/\/products\/([^/?#]+)/);
        if (pm) { type = type || "product"; handle = decodeURIComponent(pm[1]); }
        var cm = path.match(/\/collections\/([^/?#]+)/);
        if (cm && cm[1] !== "all") { type = type || "collection"; handle = handle || decodeURIComponent(cm[1]); }
        else if (/\/collections(\/|$)/.test(path)) { type = type || "collection"; }
      } catch (e) {}
      return { type: type, handle: handle };
    }

    // ── event queue + transport ───────────────────────────────────────────
    var queue = [];

    function baseEvent(type, extra) {
      var u = utm();
      var ev = {
        type: type,
        ts: Date.now(),
        platform: "web",
        anonId: anonId,
        sessionId: sessionId,
        path: location.pathname,
        locale: localeInfo(),
        referrerHost: referrerHost(),
        utmSource: u.utmSource,
        utmMedium: u.utmMedium,
        utmCampaign: u.utmCampaign,
        utmContent: u.utmContent
      };
      if (extra) {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) { ev[k] = extra[k]; }
        }
      }
      return ev;
    }

    function flush() {
      try {
        if (!queue.length) { return; }
        var batch = queue.splice(0, queue.length);
        var json = JSON.stringify({
          property: PROPERTY, source: "web", events: batch
        });
        var sent = false;
        try {
          if (navigator.sendBeacon) {
            sent = navigator.sendBeacon(
              ENDPOINT, new Blob([json], { type: "text/plain" })
            );
          }
        } catch (e) {}
        if (!sent) {
          try {
            fetch(ENDPOINT, {
              method: "POST", keepalive: true, body: json,
              headers: { "Content-Type": "text/plain" }, mode: "no-cors"
            });
          } catch (e) {}
        }
      } catch (e) {}
    }

    function enqueue(type, extra) {
      try {
        queue.push(baseEvent(type, extra));
        if (queue.length >= MAX_BATCH) { flush(); }
      } catch (e) {}
    }

    // ── engagement timer (GA4-style active/visible time) ──────────────────
    var engageMs = 0;
    var lastTick = Date.now();
    var isVisible = true;
    try { isVisible = document.visibilityState === "visible"; } catch (e) {}

    function accrue() {
      var now = Date.now();
      if (isVisible) { engageMs += now - lastTick; }
      lastTick = now;
    }
    function emitEngagement() {
      accrue();
      if (engageMs >= MIN_ENGAGE_MS) {
        enqueue("page_engagement", { durationMs: engageMs });
        engageMs = 0;
      }
    }

    // ── pageview (SPA-safe) ───────────────────────────────────────────────
    function pageview() {
      var ctx = pageContext();
      enqueue("pageview", ctx.handle ? { meta: { handle: ctx.handle } } : null);
      if (ctx.type === "product") {
        enqueue("product_view", ctx.handle ? { meta: { handle: ctx.handle } } : null);
      } else if (ctx.type === "collection") {
        enqueue("collection_view", ctx.handle ? { meta: { handle: ctx.handle } } : null);
      }
    }

    function onNavigate() {
      // Fires when the theme does client-side nav. Close out engagement for
      // the previous view, then record the new one.
      emitEngagement();
      lastTick = Date.now();
      pageview();
    }

    // ── delegated interaction listeners ───────────────────────────────────
    function bindInteractions() {
      try {
        document.addEventListener("submit", function (e) {
          try {
            var f = e.target;
            if (f && f.tagName === "FORM" && f.action &&
                f.action.indexOf("/cart/add") !== -1) {
              enqueue("add_to_cart", { meta: {} });
            }
          } catch (err) {}
        }, true);

        document.addEventListener("click", function (e) {
          try {
            var el = e.target && e.target.closest &&
              e.target.closest('[name="add"],form[action*="/cart/add"] [type="submit"],[name="checkout"],a[href*="/checkout"]');
            if (!el) { return; }
            if (el.name === "checkout" ||
                (el.getAttribute && (el.getAttribute("href") || "").indexOf("/checkout") !== -1)) {
              enqueue("begin_checkout", { meta: {} });
            } else if (el.name === "add" ||
                       (el.closest && el.closest('form[action*="/cart/add"]'))) {
              enqueue("add_to_cart", { meta: {} });
            }
          } catch (err) {}
        }, true);
      } catch (e) {}
    }

    // ── history hooks for SPA navigation ──────────────────────────────────
    function hookHistory() {
      try {
        var wrap = function (name) {
          var orig = history[name];
          if (typeof orig !== "function") { return; }
          history[name] = function () {
            var r = orig.apply(this, arguments);
            try { setTimeout(onNavigate, 0); } catch (e) {}
            return r;
          };
        };
        wrap("pushState");
        wrap("replaceState");
        window.addEventListener("popstate", function () {
          try { setTimeout(onNavigate, 0); } catch (e) {}
        });
      } catch (e) {}
    }

    // ── lifecycle wiring ──────────────────────────────────────────────────
    function start() {
      try {
        pageview();
        bindInteractions();
        hookHistory();

        document.addEventListener("visibilitychange", function () {
          try {
            accrue();
            var hidden = document.visibilityState !== "visible";
            isVisible = !hidden;
            if (hidden) { emitEngagement(); flush(); }
            else { lastTick = Date.now(); }
          } catch (e) {}
        });

        window.addEventListener("pagehide", function () {
          try { emitEngagement(); flush(); } catch (e) {}
        });

        setInterval(flush, FLUSH_MS);
      } catch (e) {}
    }

    // Run off the critical path.
    function boot() {
      try {
        if (window.requestIdleCallback) { requestIdleCallback(start, { timeout: 2000 }); }
        else { setTimeout(start, 0); }
      } catch (e) { start(); }
    }
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", boot, { once: true });
    } else {
      boot();
    }
  } catch (e) { /* never break the storefront */ }
})();
